import type { GetApiV1ThreeLetterCodeClientAdminsClient200 } from '@fukuri-admins/apis/client/gen/model';
import type { ShippingConfirmationDialogPresentation } from '../../presentations';

type PresentationProps = React.ComponentProps<typeof ShippingConfirmationDialogPresentation>;

type Args = {
  data: GetApiV1ThreeLetterCodeClientAdminsClient200;
  isMutating: boolean;
  onConfirm: PresentationProps['onConfirm'];
  onEdit: PresentationProps['onEdit'];
};

type IConvertToPresentationProps = (args: Args) => PresentationProps;

/**
 * 郵便番号にハイフンを挿入して整形する
 *
 * @param postalCode - ハイフンなし7桁の郵便番号
 * @returns ハイフン付き郵便番号（例: "100-0001"）
 */
const formatPostalCode = (postalCode: string): string => {
  if (postalCode.length !== 7) {
    return postalCode;
  }
  return `${postalCode.slice(0, 3)}-${postalCode.slice(3)}`;
};

/**
 * API レスポンスと Container 側の state / コールバックを Presentation Props に変換
 *
 * @param args.data - クライアント情報 API レスポンス
 * @param args.isMutating - 確定 API 実行中かどうか（Presentation の isConfirming に変換する）
 * @param args.onConfirm - 「この内容で確定」ボタン押下時のコールバック
 * @param args.onEdit - 「内容を修正」ボタン押下時のコールバック
 * @returns Presentation に渡す Props
 */
export const convertToPresentationProps: IConvertToPresentationProps = ({
  data,
  isMutating,
  onConfirm,
  onEdit,
}) => {
  const { client } = data;

  // shipping_address が存在することは shouldShowDialog で保証されているが、
  // 型上は optional なので念のため null チェック
  if (!client.shipping_address) {
    throw new Error('shipping_address is required to show dialog');
  }

  return {
    // TODO(MMT-522): 確認期限日フィールドが API に追加されたら「X月X日までに…」を整形して表示する
    title: '登録案内カードの発行内容をご確認ください',
    employeeCount: client.employee_count ?? undefined,
    shippingAddress: {
      postalCode: formatPostalCode(client.shipping_address.postal_code),
      prefecture: client.shipping_address.prefecture.name,
      city: client.shipping_address.city,
      streetAddress: client.shipping_address.street_address,
    },
    isConfirming: isMutating,
    onConfirm,
    onEdit,
  };
};
